import { useLayoutEffect, type RefObject } from 'react';

/*
 * Scoped reveal engine (Web Animations API, explicit keyframes).
 * Lifecycle per target: ready (inline hidden style) → observed → animating → completed (inline styles cleared).
 * Opt-in markers:
 *  data-reveal="heading" | data-reveal="image" | data-reveal-group
 * Debug: add ?motionDebug=1 to the URL for a small stats panel + console logs.
 */
const EASE = 'cubic-bezier(0.215, 0.61, 0.355, 1)'; // power3.out

interface Stats { mounted: boolean; targets: number; observed: number; triggered: number; completed: number; reduced: boolean }

export default function useRevealMotion(rootRef: RefObject<HTMLElement | null>, routeKey: string) {
  // Layout effect: hidden starting states must be applied BEFORE the first paint,
  // otherwise images/cards render visible for one frame, then get hidden, then animate in (flash).
  useLayoutEffect(() => {
    const root = rootRef.current;
    const debug = new URLSearchParams(window.location.search).get('motionDebug') === '1';
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const stats: Stats = { mounted: !!root, targets: 0, observed: 0, triggered: 0, completed: 0, reduced };
    let panel: HTMLDivElement | null = null;
    const report = () => {
      if (!debug) return;
      if (!panel) {
        panel = document.createElement('div');
        panel.setAttribute('style', 'position:fixed;right:12px;bottom:12px;z-index:99999;background:#fff;border:1px solid #ccc;border-radius:8px;padding:8px 10px;font:12px/1.5 monospace;color:#222;pointer-events:none');
        document.body.appendChild(panel);
      }
      panel.textContent = `motion ${routeKey} | mounted:${stats.mounted} targets:${stats.targets} observed:${stats.observed} triggered:${stats.triggered} completed:${stats.completed} reduced:${stats.reduced}`;
    };
    const supported = !!root && typeof IntersectionObserver !== 'undefined' && typeof Element.prototype.animate === 'function';
    if (!root || !supported || reduced) {
      report();
      if (debug) console.info('[motion] skipped', { supported, reduced });
      return () => panel?.remove();
    }

    let disposed = false;
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    const lineY = mobile ? 12 : 22;
    const cardY = mobile ? 10 : 16;
    const step = mobile ? 70 : 100;
    const anims: Animation[] = [];
    const restores = new Map<HTMLElement, string>();
    const hidden = new Set<HTMLElement>();

    const hide = (el: HTMLElement, css: Partial<CSSStyleDeclaration>) => { Object.assign(el.style, css); hidden.add(el); };
    const clear = (el: HTMLElement) => {
      el.style.removeProperty('opacity');
      el.style.removeProperty('transform');
      el.style.removeProperty('clip-path');
      hidden.delete(el);
    };
    const run = (el: HTMLElement, frames: Record<string, string | number>[], duration: number, delay: number, onDone?: () => void) => {
      const a = el.animate(frames, { duration, delay, easing: EASE, fill: 'both' });
      anims.push(a);
      a.finished.then(() => {
        if (disposed) return;
        clear(el);
        a.cancel();
        onDone?.();
      }).catch(() => clear(el));
    };
    const done = () => { stats.completed += 1; report(); };

    const headings = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal="heading"]'));
    const images = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal="image"]'));
    const groups = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal-group]'));
    stats.targets = headings.length + images.length + groups.length;

    const playHeading = (el: HTMLElement) => {
      const inners = Array.from(el.querySelectorAll<HTMLElement>('.rv-line-inner'));
      if (!inners.length) {
        // Not split (layout not measurable) - simple fade, never a frozen line layout.
        run(el, [{ opacity: 0 }, { opacity: 1 }], 820, 0, done);
        return;
      }
      clear(el);
      let left = inners.length;
      inners.forEach((inner, i) => run(inner, [
        { opacity: 0, transform: `translateY(${lineY}px)` },
        { opacity: 1, transform: 'translateY(0)' },
      ], 820, i * step, () => {
        left -= 1;
        if (left === 0) {
          const original = restores.get(el);
          if (original !== undefined) el.textContent = original;
          restores.delete(el);
          el.removeAttribute('aria-label');
          done();
        }
      }));
    };

    const imageStarted = new Set<HTMLElement>();
    const playImage = (el: HTMLElement) => {
      if (imageStarted.has(el)) return;
      imageStarted.add(el);
      const img = el.querySelector('img');
      let started = false;
      const start = () => {
        if (started || disposed) return;
        started = true;
        run(el, [{ clipPath: 'inset(100% 0 0 0)' }, { clipPath: 'inset(0 0 0 0)' }], 950, 0, done);
        if (img) run(img, [{ transform: 'scale(1.04)' }, { transform: 'scale(1)' }], 950, 0);
      };
      if (!img || img.complete) { start(); return; }
      img.addEventListener('load', start, { once: true });
      img.addEventListener('error', start, { once: true });
      window.setTimeout(start, 2500);
    };

    const playGroup = (el: HTMLElement) => {
      const kids = Array.from(el.children) as HTMLElement[];
      let left = kids.length;
      if (!left) { done(); return; }
      kids.forEach((k, i) => run(k, [
        { opacity: 0, transform: `translateY(${cardY}px)` },
        { opacity: 1, transform: 'translateY(0)' },
      ], 620, i * step, () => { left -= 1; if (left === 0) done(); }));
    };

    // Image containers are clipped to 0 height while waiting, which makes IntersectionObserver
    // treat them as never visible. So we observe their unclipped parent instead.
    const imageByProxy = new Map<Element, HTMLElement[]>();

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const proxied = imageByProxy.get(entry.target);
        if (proxied) {
          io.unobserve(entry.target);
          imageByProxy.delete(entry.target);
          proxied.forEach((imgEl) => { stats.triggered += 1; playImage(imgEl); });
          report();
          return;
        }
        const el = entry.target as HTMLElement;
        io.unobserve(el);
        stats.triggered += 1;
        report();
        if (debug) console.info('[motion] trigger', el.dataset.reveal ?? 'group', el);
        if (el.dataset.reveal === 'heading') { splitHeading(el); playHeading(el); }
        else if (el.dataset.reveal === 'image') playImage(el);
        else playGroup(el);
      });
    }, { threshold: 0.15 });
    const observe = (el: HTMLElement) => { io.observe(el); stats.observed += 1; };

    images.forEach((el) => {
      hide(el, { clipPath: 'inset(100% 0 0 0)' });
      const img = el.querySelector('img');
      if (img) hide(img, { transform: 'scale(1.04)' });
      const proxy = el.parentElement ?? root;
      const list = imageByProxy.get(proxy) ?? [];
      list.push(el);
      imageByProxy.set(proxy, list);
      if (list.length === 1) io.observe(proxy);
      stats.observed += 1;
    });

    // Safety net 1: geometry check, independent of IntersectionObserver.
    const checkImages = () => {
      images.forEach((el) => {
        if (imageStarted.has(el)) return;
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight * 0.9 && r.bottom > 0) { stats.triggered += 1; playImage(el); }
      });
    };
    const geoTimer = window.setInterval(checkImages, 400);
    // Safety net 2: after 3s, images that are on screen or already scrolled past are shown;
    // images further down keep waiting so they still animate when reached.
    const hardTimer = window.setTimeout(() => {
      images.forEach((el) => {
        if (imageStarted.has(el)) return;
        if (el.getBoundingClientRect().top >= window.innerHeight) return;
        imageStarted.add(el);
        clear(el);
        const img = el.querySelector('img');
        if (img) clear(img);
      });
    }, 3000);
    groups.forEach((el) => {
      Array.from(el.children).forEach((k) => hide(k as HTMLElement, { opacity: '0', transform: `translateY(${cardY}px)` }));
      observe(el);
    });

    const splitHeading = (el: HTMLElement) => {
      const original = el.textContent ?? '';
      const words = original.split(/\s+/).filter(Boolean);
      if (!words.length) return;
      // Line breaks are frozen from a measurement. If layout isn't settled yet (zero/tiny width),
      // every word would be measured as its own line -> vertical text. Skip splitting then.
      const width = el.getBoundingClientRect().width;
      const widest = Math.max(...words.map((w) => w.length));
      const fontPx = parseFloat(getComputedStyle(el).fontSize) || 16;
      if (width < widest * fontPx * 0.6 * 2) return;
      restores.set(el, original);
      el.textContent = '';
      const spans = words.map((w, i) => {
        const s = document.createElement('span');
        s.style.display = 'inline-block';
        s.textContent = w;
        el.appendChild(s);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
        return s;
      });
      const lines: string[][] = [];
      let top = Number.NaN;
      spans.forEach((s) => {
        if (!lines.length || Math.abs(s.offsetTop - top) > 2) { lines.push([]); top = s.offsetTop; }
        lines[lines.length - 1].push(s.textContent ?? '');
      });
      el.textContent = '';
      el.setAttribute('aria-label', original);
      lines.forEach((ws) => {
        const mask = document.createElement('span');
        mask.className = 'rv-line';
        const inner = document.createElement('span');
        inner.className = 'rv-line-inner';
        inner.textContent = ws.join(' ');
        hide(inner, { opacity: '0', transform: `translateY(${lineY}px)` });
        mask.appendChild(inner);
        el.appendChild(mask);
      });
    };

    headings.forEach((el) => hide(el, { opacity: '0' }));
    const fontsReady = document.fonts?.ready ?? Promise.resolve();
    fontsReady.then(() => {
      if (disposed) return;
      // Split lazily at trigger time (in view, layout settled) instead of right after mount.
      headings.forEach((el) => observe(el));
      report();
    }).catch(() => headings.forEach(clear));

    report();
    if (debug) console.info('[motion] mounted', { ...stats });

    return () => {
      disposed = true;
      window.clearInterval(geoTimer);
      window.clearTimeout(hardTimer);
      io.disconnect();
      anims.forEach((a) => a.cancel());
      restores.forEach((original, el) => { el.textContent = original; el.removeAttribute('aria-label'); });
      restores.clear();
      Array.from(hidden).forEach(clear);
      panel?.remove();
    };
  }, [rootRef, routeKey]);
}